import devLogger from 'react-utils/devLogger';
import getComponentName from 'react-utils/getComponentName';
import { attachWrappedComponent, copyMethods, isFunctionalComponent } from './utils';
import refObject from '../utils/propTypes/refObject';
import { stopPropagationHandler } from '../utils/DomEvents';
import { Component, createRef } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
let hasPingedNewRelic = false;
const reportDeprecatedMethod = (componentName, methodName) => {
  if (process.env.NODE_ENV !== 'production') {
    devLogger.warn({
      message: `${componentName}: Calling \`${methodName}\` on a ref to an input component is deprecated. ` + `Use \`inputRef\` to get a direct reference to the <input> or <textarea> instead.`,
      key: `ShareInput-${methodName}`
    });
  }
  if (window.newrelic && !hasPingedNewRelic) {
    window.newrelic.addPageAction('share-hoc-method', {
      hoc: 'ShareInput',
      componentName,
      methodName
    });
    hasPingedNewRelic = true;
  }
};
export default function (InputComponent) {
  class ShareInputDecorator extends Component {
    constructor(props) {
      super(props);
      this.focus = () => {
        if (this._ref && typeof this._ref.focus === 'function') {
          this._ref.focus();
          return;
        }
        reportDeprecatedMethod(InputComponent.displayName, 'focus');
        const inputRef = this.props.inputRef || this._defaultInputRef;
        if (inputRef.current && typeof inputRef.current.focus === 'function') {
          inputRef.current.focus();
        }
      };
      this.select = () => {
        if (this._ref && typeof this._ref.select === 'function') {
          this._ref.select();
          return;
        }
        reportDeprecatedMethod(InputComponent.displayName, 'select');
        const inputRef = this.props.inputRef || this._defaultInputRef;
        if (inputRef.current && typeof inputRef.current.select === 'function') {
          inputRef.current.select();
        }
      };
      this.refCallback = ref => {
        if (isFunctionalComponent(InputComponent)) return;
        this._ref = ref;
        if (ref) copyMethods(ref, this);
      };
      this.handleCompositionStart = evt => {
        const {
          onCompositionStart
        } = this.props;

        // Prevent keyup/keydown while IME composition is in progress
        const inputEl = this.getInputRef().current;
        if (inputEl === null) {
          return;
        }
        inputEl.addEventListener('keyup', stopPropagationHandler);
        inputEl.addEventListener('keydown', stopPropagationHandler);
        if (onCompositionStart) onCompositionStart(evt);
      };
      this.handleCompositionEnd = evt => {
        const {
          onCompositionEnd
        } = this.props;

        // Resume allowing keyup/keydown events
        const inputEl = this.getInputRef().current;
        if (inputEl === null) {
          return;
        }
        inputEl.removeEventListener('keyup', stopPropagationHandler);
        inputEl.removeEventListener('keydown', stopPropagationHandler);
        if (onCompositionEnd) onCompositionEnd(evt);
      };
      this._defaultInputRef = /*#__PURE__*/createRef();
      this._ref = null;
    }
    getInputRef() {
      return this.props.inputRef || this._defaultInputRef;
    }
    render() {
      return /*#__PURE__*/_jsx(InputComponent, Object.assign({}, this.props, {
        onCompositionStart: this.handleCompositionStart,
        onCompositionEnd: this.handleCompositionEnd,
        inputRef: this.getInputRef(),
        ref: this.refCallback
      }));
    }
  }

  // @ts-expect-error getComponentName does not accept ComponentType<T>
  const componentName = getComponentName(InputComponent);
  ShareInputDecorator.displayName = `ShareInput(${componentName})`;
  ShareInputDecorator.propTypes = Object.assign({}, InputComponent.propTypes, {
    inputRef: refObject
  });
  ShareInputDecorator.defaultProps = InputComponent.defaultProps;
  attachWrappedComponent(ShareInputDecorator, InputComponent);
  return ShareInputDecorator;
}